<template>
  <div class="user-list-container">
    <div class="page-header"><h2><span class="breadcrumb-level">系统管理</span><span class="breadcrumb-sep">/</span><span class="breadcrumb-level">账号权限</span><span class="breadcrumb-sep">/</span><span class="breadcrumb-level breadcrumb-current">管理员账户</span></h2></div>
    <el-card shadow="hover">
      <div class="card-header">
        <div class="filter-group">
          <el-input
            v-model="searchQuery"
            placeholder="搜索用户名或邮箱"
            style="width: 300px; margin-right: 10px;"
            clearable
          >
            <template #prefix>
              <el-icon><search /></el-icon>
            </template>
          </el-input>
          <el-button type="primary" @click="handleSearch">
            <el-icon><search /></el-icon> 搜索
          </el-button>
        </div>
        <el-button type="primary" @click="handleCreate">
          <el-icon><plus /></el-icon> 添加用户
        </el-button>
      </div>
      
      <el-table :data="users" border stripe style="width: 100%" v-loading="loading">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="name" label="用户名" />
        <!-- <el-table-column prop="email" label="邮箱" /> -->
        <el-table-column prop="phone" label="电话" />
        <el-table-column prop="role" label="角色">
          <template #default="scope">
            <template v-if="Array.isArray(scope.row.roles)">
              <el-tag v-for="role in scope.row.roles" :key="role?.id" size="small" style="margin-right: 5px;">
                {{ role?.name || '' }}
              </el-tag>
            </template>
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100">
          <template #default="scope">
            <el-tag :type="scope.row.status ? 'success' : 'danger'">
              {{ scope.row.status ? '启用' : '禁用' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" width="180">
          <template #default="{ row }">
            {{ formatDate(row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="200" fixed="right">
          <template #default="{ row }">
            <!-- 超级管理员不允许编辑和删除 -->
            <template v-if="!row.is_super_admin">
              <el-button 
                type="primary" 
                size="small" 
                @click="handleEdit(row)"
                style="margin-right: 5px;"
              >
                <el-icon><edit /></el-icon> 编辑
              </el-button>
              <el-button 
                type="danger" 
                size="small" 
                @click="handleDelete(row)"
              >
                <el-icon><delete /></el-icon> 删除
              </el-button>
            </template>
            <!-- 超级管理员显示不可操作提示 -->
            <el-tag v-else type="info" size="small">超级管理员</el-tag>
          </template>
        </el-table-column>
      </el-table>
      
      <div class="card-footer">
        <el-pagination
          :current-page="currentPage"
          :page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>
    
    <!-- 用户编辑抽屉 -->
    <el-drawer
      v-model="drawerVisible"
      title="用户编辑"
      size="50%"
      direction="rtl"
      :before-close="handleDrawerClose"
    >
      <el-form
        ref="userFormRef"
        :model="userForm"
        :rules="rules"
        label-position="top"
        label-width="120px"
      >
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="用户名" prop="name">
              <el-input v-model="userForm.name" placeholder="请输入用户名" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="邮箱" prop="email">
              <el-input v-model="userForm.email" type="email" placeholder="请输入邮箱" />
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="密码" prop="password" v-if="!isEditMode">
              <el-input v-model="userForm.password" type="password" placeholder="请输入密码" show-password />
            </el-form-item>
            <el-form-item label="密码（可选）" v-else>
              <el-input v-model="userForm.password" type="password" placeholder="请输入密码，不修改则留空" show-password />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="确认密码" prop="password_confirmation" v-if="!isEditMode">
              <el-input v-model="userForm.password_confirmation" type="password" placeholder="请确认密码" show-password />
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="电话" prop="phone">
              <el-input v-model="userForm.phone" placeholder="请输入电话" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="角色" prop="role_ids">
              <el-select v-model="userForm.role_ids" multiple placeholder="请选择角色">
                <el-option v-for="role in (roles || [])" :key="role?.id" :label="role?.name || ''" :value="role?.id || ''" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="状态" prop="status">
              <el-radio-group v-model="userForm.status">
                <el-radio :label="1">启用</el-radio>
                <el-radio :label="0">禁用</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>
        
        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label="备注">
              <el-input v-model="userForm.notes" type="textarea" placeholder="请输入备注信息" rows="4" />
            </el-form-item>
          </el-col>
        </el-row>
        
        <div class="form-actions">
          <el-button @click="handleDrawerClose">取消</el-button>
          <el-button type="primary" @click="handleFormSubmit">提交</el-button>
        </div>
      </el-form>
    </el-drawer>
  </div>
</template>

<script>
import { ref, onMounted, reactive } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Search, Plus, Edit, Delete } from '@element-plus/icons-vue';
import * as accountApi from '../../api/account';

export default {
  name: 'UserList',
  components: {
    Search,
    Plus,
    Edit,
    Delete
  },
  setup() {
    // 列表数据
    const users = ref([]);
    const searchQuery = ref('');
    const currentPage = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const loading = ref(false);
    
    // 抽屉相关状态
    const drawerVisible = ref(false);
    const isEditMode = ref(false);
    const userFormRef = ref(null);
    const editingUserId = ref(null);
    
    // 用户表单数据
    const userForm = reactive({
      name: '',
      email: '',
      password: '',
      password_confirmation: '',
      phone: '',
      role_ids: [],
      status: 1,
      notes: ''
    });
    
    // 角色列表
    const roles = ref([]);
    
    // 表单验证规则
    const rules = {
      name: [
        { required: true, message: '请输入用户名', trigger: 'blur' },
        { min: 3, max: 20, message: '用户名长度在 3 到 20 个字符', trigger: 'blur' }
      ],
      phone: [
        { pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码', trigger: 'blur' }
      ],
      role_ids: [
        { required: true, message: '请选择角色', trigger: 'change' }
      ],
      password: [
        { min: 8, max: 20, message: '密码长度在 8 到 20 个字符', trigger: 'blur' }
      ],
      password_confirmation: [
        {
          validator: (rule, value, callback) => {
            if (value !== userForm.password) {
              callback(new Error('两次输入的密码不一致'));
            } else {
              callback();
            }
          },
          trigger: 'blur'
        }
      ]
    };
    
    // 获取后台账号列表
    const fetchUsers = async () => {
      loading.value = true;
      try {
        const response = await accountApi.fetchAccounts({
          page: currentPage.value,
          pageSize: pageSize.value,
          search: searchQuery.value
        });
        // 确保users始终是数组，避免Element Plus表格出错（兼容两种分页结构）
        users.value = Array.isArray(response.data) ? response.data : (Array.isArray(response.data?.data) ? response.data.data : []);
        total.value = response.pagination?.total || response.data?.total || 0;
      } catch (error) {
        ElMessage.error('获取后台账号列表失败');
        console.error(error);
        // 出错时确保users是数组
        users.value = [];
        total.value = 0;
      } finally {
        loading.value = false;
      }
    };
    
    // 获取角色列表
    const fetchRoles = async () => {
      try {
        const response = await accountApi.fetchRoles();
        // 由于axios拦截器已经处理了response.data，所以直接使用response
        roles.value = Array.isArray(response.data) ? response.data : [];
      } catch (error) {
        ElMessage.error('获取角色列表失败');
        console.error(error);
        // 出错时确保roles是数组
        roles.value = [];
      }
    };
    
    // 页面加载时获取数据
    onMounted(() => {
      fetchUsers();
      fetchRoles();
    });
    
    // 打开创建用户抽屉
    const handleCreate = async () => {
      isEditMode.value = false;
      // 重置表单
      Object.assign(userForm, {
        name: '',
        email: '',
        password: '',
        password_confirmation: '',
        phone: '',
        role_ids: [],
        status: 1,
        notes: ''
      });
      // 点击添加时重新获取角色列表
      await fetchRoles();
      drawerVisible.value = true;
    };
    
    // 打开编辑用户抽屉
    const handleEdit = async (user) => {
      isEditMode.value = true;
      editingUserId.value = user.id;
      // 点击编辑时重新获取角色列表
      await fetchRoles();
      // 填充表单数据，确保roles是有效的数组且不包含null值
      const validRoles = Array.isArray(user.roles) ? user.roles.filter(role => role) : [];
      Object.assign(userForm, {
        name: user.name,
        email: user.email,
        password: '',
        password_confirmation: '',
        phone: user.phone,
        role_ids: validRoles.map(role => role.id),
        status: user.status ? 1 : 0,
        notes: user.notes || ''
      });
      drawerVisible.value = true;
    };
    
    // 关闭抽屉
    const handleDrawerClose = () => {
      drawerVisible.value = false;
    };
    
    // 表单提交
    const handleFormSubmit = async () => {
      if (!userFormRef.value) return;
      
      await userFormRef.value.validate(async (valid) => {
        if (valid) {
          try {
            // 移除未修改的密码字段
            const submitData = { ...userForm };
            if (!submitData.password) {
              delete submitData.password;
              delete submitData.password_confirmation;
            }
            
            if (isEditMode.value) {
              // 编辑模式
              await accountApi.updateAccount(editingUserId.value, submitData);
              ElMessage.success('后台账号更新成功');
            } else {
              // 创建模式
              await accountApi.addAccount(submitData);
              ElMessage.success('后台账号创建成功');
            }
            
            // 关闭抽屉并刷新列表
            drawerVisible.value = false;
            fetchUsers();
          } catch (error) {
            ElMessage.error(isEditMode.value ? '后台账号更新失败' : '后台账号创建失败');
            console.error(error);
          }
        }
      });
    };
    
    // 删除后台账号
    const handleDelete = (user) => {
      ElMessageBox.confirm('确定要删除该后台账号吗？', '警告', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await accountApi.deleteAccount(user.id);
          ElMessage.success('删除成功');
          fetchUsers();
        } catch (error) {
          ElMessage.error('删除失败');
          console.error(error);
        }
      }).catch(() => {
        // 取消删除操作
      });
    };
    
    // 处理分页大小变化
    const handleSizeChange = (size) => {
      pageSize.value = size;
      fetchUsers();
    };
    
    // 处理搜索
    const handleSearch = () => {
      currentPage.value = 1;
      fetchUsers();
    };
    
    // 处理当前页变化
    const handleCurrentChange = (current) => {
      currentPage.value = current;
      fetchUsers();
    };
    
    // 格式化日期
    const formatDate = (date) => {
      if (!date) return '-';
      const d = new Date(date);
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      // 列表数据
      users,
      searchQuery,
      currentPage,
      pageSize,
      total,
      loading,
      // 抽屉相关
      drawerVisible,
      isEditMode,
      userForm,
      userFormRef,
      roles,
      rules,
      // 方法
      handleCreate,
      handleEdit,
      handleDrawerClose,
      handleFormSubmit,
      handleDelete,
      handleSizeChange,
      handleCurrentChange,
      handleSearch,
      formatDate
    };
  }
};
</script>

<style scoped>




.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.filter-group {
  display: flex;
  align-items: center;
}

.card-footer {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 30px;
}

.form-actions .el-button {
  margin-left: 10px;
}
</style>